import { useEffect, useRef } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useAgTechAuthContext } from "@agtech/core/auth/AgTechAuthContext";
import { useAgTechDataContext } from "@agtech/core/data/common/AgTechDataContext";
import { logDev, logDevError } from "@agtech/core/logging/AgTechLogger";
import { useCurrentSale } from "../sales/SaleDataStore";
import { LotDataGetLotsQuery } from "../lots/LotDataQueries";
import { BidderDataGetSaleBiddersQuery } from "../bidders/BidderDataQueries";
import { InvoiceQueryKey } from "../invoicing/InvoicingDataQueries";
import { PlanningTaskDataGetMyTasksQuery, PlanningTaskDataGetSaleTasksQuery } from "../planningTasks/PlanningTaskQueries";
import { useAppContext } from "@agtech/core/app/AgTechAppContext";

// Pushes from the backend (SalesProject.Events.EventService, via SaleHub) carry only a
// partial DTO of what changed, not the full entity shape the grids read from the query
// cache. Rather than hand-merge partial fields, treat each push as a signal to refetch -
// this keeps the client in sync with whatever the server actually persisted.
export const useSaleRealtimeConnection = () => {
    const { baseURL } = useAgTechDataContext();
    const { user } = useAgTechAuthContext();
    const queryClient = useQueryClient();
    const currentSale = useCurrentSale();
    const appContext = useAppContext();

    const tokenRef = useRef(user.token);
    tokenRef.current = user.token;

    const saleIdRef = useRef(currentSale.id);
    saleIdRef.current = currentSale.id;

    // useAppContext()'s value isn't guaranteed stable across renders (same reasoning as
    // tokenRef/saleIdRef above) - read messaging through a ref so showing a toast can't force
    // this effect to tear down and reconnect the hub.
    const messagingRef = useRef(appContext.messaging);
    messagingRef.current = appContext.messaging;

    const hasToken = !!user.token;

    useEffect(() => {
        if (!baseURL || !hasToken) {
            return;
        }

        const connection = new HubConnectionBuilder()
            .withUrl(`${baseURL}/saleHub`, {
                accessTokenFactory: () => tokenRef.current
            })
            .withAutomaticReconnect()
            .build();

        connection.on("LotSold", () => {
            queryClient.invalidateQueries({
                queryKey: LotDataGetLotsQuery.queryKey({ saleId: saleIdRef.current })
            });
        });

        // Fired for lot changes other than a sale (split, split-update, reorder, reversing a
        // purchase) - see SalesProject.Events.EventService.LotsChangedEvent.
        connection.on("LotsChanged", () => {
            queryClient.invalidateQueries({
                queryKey: LotDataGetLotsQuery.queryKey({ saleId: saleIdRef.current })
            });
        });

        connection.on("RegisterBidder", () => {
            queryClient.invalidateQueries({
                queryKey: BidderDataGetSaleBiddersQuery.queryKey({ saleId: saleIdRef.current })
            });
        });

        // Fired for bidder changes other than registration (number/contact edit, contact change,
        // removal) - see SalesProject.Events.EventService.BiddersChangedEvent.
        connection.on("BiddersChanged", () => {
            queryClient.invalidateQueries({
                queryKey: BidderDataGetSaleBiddersQuery.queryKey({ saleId: saleIdRef.current })
            });
        });

        // Fired for any invoice mutation (creation, payments, lot/credit/modifier changes) - see
        // SalesProject.Events.EventService.InvoiceChangedEvent. No invoiceId is carried, so this
        // invalidates every query-key prefix invoicing data lives under rather than one specific
        // invoice - the same "just refetch broadly" approach LotsChanged above takes for lots.
        connection.on("InvoiceChanged", () => {
            queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            queryClient.invalidateQueries({ queryKey: ['Payments'] });
            queryClient.invalidateQueries({ queryKey: ['InvoiceLots'] });
            queryClient.invalidateQueries({ queryKey: ['InvoiceCredits'] });
            queryClient.invalidateQueries({ queryKey: ['InvoiceInvoiceModifiers'] });
        });

        // Fired by PlanningTaskNotificationBackgroundService when a planning task assigned to this
        // user comes due - see SalesProject.Events.EventService.PlanningTaskDueEvent. Unlike the
        // events above, this is targeted per-user (not broadcast to the whole tenant group), so it
        // gets a toast rather than a silent refetch - the user needs to actually notice it.
        connection.on("PlanningTaskDue", (task: { saleId: number, taskName: string, saleName?: string }) => {
            queryClient.invalidateQueries({
                queryKey: PlanningTaskDataGetSaleTasksQuery.queryKey({ saleId: task.saleId })
            });

            // Also refresh the cross-sale "My Tasks" list (see MyPlanningTasksDialog) - a due task
            // notification isn't necessarily for the sale currently being viewed.
            queryClient.invalidateQueries({
                queryKey: PlanningTaskDataGetMyTasksQuery.queryKey(undefined)
            });

            messagingRef.current.showWarning({
                header: 'Planning Task Due',
                details: task.saleName ? `"${task.taskName}" (${task.saleName})` : `"${task.taskName}"`
            });
        });

        connection.start()
            .then(() => logDev("[SaleHub] Connected"))
            .catch(error => logDevError("[SaleHub] Failed to connect", error));

        return () => {
            connection.stop();
        };
    }, [baseURL, hasToken, queryClient]);
}
